import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useEffect, useState } from "react";
import { useUserData } from "src/context/userContext";
import { stat_getLasku1 } from "src/data/stat/statFunctions";
import {
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

export default function Stat_Kustannuspaikat() {
  const { token, userAsiakas } = useUserData();
  const [data, setData] = useState([]);
  const [selectedKustannusPaikka, setSelectedKustannusPaikka] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  useEffect(() => {
    if (!userAsiakas) return;
    stat_getLasku1(userAsiakas?.asiakasId, token).then((data) => setData(data));
  }, [userAsiakas]);

  const displayData = data;
  // Prepare data for charts
  const aggregatedData = [];

  displayData.forEach((item) => {
    const dateKey = `${item.year}-${item.month}`;
    let existing = aggregatedData.find((d) => d.date === dateKey);
    if (!existing) {
      existing = { date: dateKey };
      aggregatedData.push(existing);
    }
    existing[item.kustannusPaikka] = item.eur;
  });

  // Get list of kustannusPaikka for the filter
  const kustannusPaikkaList = Array.from(new Set(displayData.map((item) => item.kustannusPaikka)));

  // Get list of available months and years
  const monthList = Array.from(new Set(displayData.map((item) => item.month))).sort(
    (a, b) => a - b
  );

  const yearList = Array.from(new Set(displayData.map((item) => item.year))).sort((a, b) => a - b);

  // Set default selected month and year if not set
  useEffect(() => {
    if (selectedMonth === "" && monthList.length > 0) {
      setSelectedMonth(monthList[monthList.length - 1]);
    }
    if (selectedYear === "" && yearList.length > 0) {
      setSelectedYear(yearList[yearList.length - 1]);
    }
  }, [monthList, yearList, selectedMonth, selectedYear]);

  return (
    <div>
      {/* Line Chart */}
      <Paper sx={{ padding: 2 }}>
        {/* <Typography variant="h6" gutterBottom>
              Monthly Expenses Line Chart
            </Typography> */}
        {aggregatedData.length > 0 ? (
          <ResponsiveContainer width="100%" height={455}>
            <LineChart data={aggregatedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              {/* Removed Legend from LineChart */}
              {Object.keys(aggregatedData[0])
                .filter((key) => key !== "date")
                .map((key, index) => {
                  const yAxisId = key === "Betoni" ? "right" : "left";
                  return (
                    <Line
                      type="monotone"
                      dataKey={key}
                      stroke={`hsl(${(index * 50) % 360}, 70%, 50%)`}
                      key={key}
                      yAxisId={yAxisId}
                    />
                  );
                })}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography>No data available for the line chart.</Typography>
        )}
      </Paper>
      {/* Bar Chart */}
      <Paper sx={{ padding: 2 }}>
        {/* <Typography variant="h6" gutterBottom>
              Monthly Expenses
            </Typography> */}
        {/* Month and Year Selection */}
        <FormControl sx={{ minWidth: 120, mr: 2 }}>
          <InputLabel>Vuosi</InputLabel>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            label="Vuosi">
            {yearList.map((year) => (
              <MenuItem value={year} key={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Kuukausi</InputLabel>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            label="Kuukausi">
            {monthList.map((month) => (
              <MenuItem value={month} key={month}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {displayData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={displayData.filter(
                (item) => item.month === selectedMonth && item.year === selectedYear
              )}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="kustannusPaikka"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-90}
                textAnchor="end"
              />
              <YAxis />
              <Tooltip />
              {/* Removed Legend from BarChart */}
              <Bar dataKey="eur" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography>No data available for the bar chart.</Typography>
        )}
      </Paper>
      {/* Data Table */}
      <Paper sx={{ padding: 2 }}>
        {/* <Typography variant="h6" gutterBottom>
              Expenses Table
            </Typography> */}
        <FormControl sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel>KustannusPaikka</InputLabel>
          <Select
            value={selectedKustannusPaikka}
            onChange={(e) => setSelectedKustannusPaikka(e.target.value)}
            label="KustannusPaikka">
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {kustannusPaikkaList.map((kp) => (
              <MenuItem value={kp} key={kp}>
                {kp}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {displayData.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vuosi</TableCell>
                  <TableCell>Kuukausi</TableCell>
                  <TableCell>KustannusPaikka</TableCell>
                  <TableCell>Eur</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData
                  .filter(
                    (row) =>
                      !selectedKustannusPaikka || row.kustannusPaikka === selectedKustannusPaikka
                  )
                  .map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.year}</TableCell>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>{row.kustannusPaikka}</TableCell>
                      {/* make with 2 decimals */}
                      <TableCell>
                        {row.eur.toLocaleString("fi-FI", { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>No data available for the table.</Typography>
        )}
      </Paper>
    </div>
  );
}
